import { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Trophy, Vote, Calendar, Users, X, Play, ExternalLink, Clock, CheckCircle2, Loader2, Crown, ScrollText, Share2 } from "lucide-react";
import { Reveal, MaskedLines } from "../components/Reveal";
import { usePageTitle } from "../components/Shared";
import { useLang } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const COPY = {
  fr: {
    meta: "Concours",
    noneTitle: "Aucun concours en cours",
    noneText: "Revenez bientôt : un nouvel événement DOC TOGO arrive très vite.",
    home: "Retour à l'accueil",
    prize: "Grand prix",
    candidates: "Les candidates",
    candidatesSub: "Votez pour votre candidate préférée. Chaque vote compte !",
    vote: "Voter",
    voted: "Vote enregistré !",
    voteFreeNote: "Vote 100% gratuit — aucun compte, aucun paiement.",
    rankLabel: "Classement",
    voteDialogTitle: "Voter pour",
    emailLabel: "Votre adresse email",
    emailPh: "vous@exemple.com",
    emailPrivacy: "Votre email sert uniquement à sécuriser votre vote. Il n'est jamais affiché publiquement.",
    sendCode: "Recevoir mon code par email",
    sendingCode: "Envoi du code…",
    codeSent: "Code envoyé ! Vérifiez votre boîte mail (et les spams).",
    codeLabel: "Code reçu par email (6 chiffres)",
    codePh: "••••••",
    confirmVote: "Valider mon vote",
    confirming: "Vérification…",
    resend: "Renvoyer un code",
    back: "Retour",
    votesClosed: "Les votes sont fermés pour le moment.",
    voteLoginFree: "Vote gratuit",
    phases: "Les phases du concours",
    endsIn: "Fin des votes dans",
    startsIn: "Début des votes dans",
    days: "j",
    hours: "h",
    minutes: "min",
    seconds: "s",
    results: "Résultats officiels",
    qualified: "Qualifiée",
    votesUnit: "votes",
    pendingResults: "Résultats en attente de validation officielle.",
    hiddenScores: "Les scores sont masqués pendant cette phase et seront révélés à la clôture.",
    winner: "La grande gagnante",
    winnerSub: (prix, devise) => `remporte le grand prix de ${prix} ${devise}`,
    register: "S'inscrire au concours",
    registerClosed: "Inscriptions closes",
    bio: "Biographie",
    motivation: "Motivation",
    watchVideo: "Voir la vidéo",
    close: "Fermer",
    rules: "Règlement du concours",
    conditions: "Conditions de participation",
    phaseOf: (n) => `Phase ${n}`,
    finale: "Finale",
    participants: (n) => `${n} candidates en compétition`,
    qualifiedCount: (n) => `${n} qualifiée(s)`,
    votesTotal: (n) => `${n} votes enregistrés`,
    loading: "Chargement du concours…",
    share: "Partager",
    shareMsg: (cand, eventName, url) =>
      `Votez pour la candidate N°${String(cand.numero).padStart(2, "0")} — ${cand.prenom} ${cand.nom} au concours « ${eventName} » ! Chaque vote compte : ${url}`,
    shareCopied: "Lien de la candidate copié !",
  },
  en: {
    meta: "Contest",
    noneTitle: "No contest currently running",
    noneText: "Come back soon: a new DOC TOGO event is on its way.",
    home: "Back to home",
    prize: "Grand prize",
    candidates: "The contestants",
    candidatesSub: "Vote for your favourite contestant. Every vote counts!",
    vote: "Vote",
    voted: "Vote recorded!",
    voteFreeNote: "100% free voting — no account, no payment.",
    rankLabel: "Rank",
    voteDialogTitle: "Vote for",
    emailLabel: "Your email address",
    emailPh: "you@example.com",
    emailPrivacy: "Your email is only used to secure your vote. It is never displayed publicly.",
    sendCode: "Email me my code",
    sendingCode: "Sending code…",
    codeSent: "Code sent! Check your inbox (and spam folder).",
    codeLabel: "Code received by email (6 digits)",
    codePh: "••••••",
    confirmVote: "Confirm my vote",
    confirming: "Verifying…",
    resend: "Resend a code",
    back: "Back",
    votesClosed: "Voting is currently closed.",
    voteLoginFree: "Free voting",
    phases: "Contest phases",
    endsIn: "Voting ends in",
    startsIn: "Voting starts in",
    days: "d",
    hours: "h",
    minutes: "min",
    seconds: "s",
    results: "Official results",
    qualified: "Qualified",
    votesUnit: "votes",
    pendingResults: "Results pending official validation.",
    hiddenScores: "Scores are hidden during this phase and will be revealed at closure.",
    winner: "The grand winner",
    winnerSub: (prix, devise) => `wins the grand prize of ${prix} ${devise}`,
    register: "Enter the contest",
    registerClosed: "Registration closed",
    bio: "Biography",
    motivation: "Motivation",
    watchVideo: "Watch the video",
    close: "Close",
    rules: "Contest rules",
    conditions: "Eligibility requirements",
    phaseOf: (n) => `Phase ${n}`,
    finale: "Final",
    participants: (n) => `${n} contestants competing`,
    qualifiedCount: (n) => `${n} qualified`,
    votesTotal: (n) => `${n} votes recorded`,
    loading: "Loading the contest…",
    share: "Share",
    shareMsg: (cand, eventName, url) =>
      `Vote for contestant N°${String(cand.numero).padStart(2, "0")} — ${cand.prenom} ${cand.nom} in the "${eventName}" contest! Every vote counts: ${url}`,
    shareCopied: "Contestant link copied!",
  },
};

const formatRang = (r, lang) => {
  if (lang === "en") {
    const s = ["th", "st", "nd", "rd"];
    const v = r % 100;
    return r + (s[(v - 20) % 10] || s[v] || s[0]);
  }
  return r === 1 ? "1ère" : `${r}e`;
};

const youtubeId = (url) => {
  const m = /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/shorts\/)([\w-]{6,})/.exec(url || "");
  return m ? m[1] : null;
};

const VideoBlock = ({ url, fileUrl, label }) => {
  if (fileUrl) {
    return <video data-testid="candidate-video" src={`${BACKEND}${fileUrl}`} controls playsInline preload="metadata" className="w-full rounded-2xl bg-black" />;
  }
  const yt = youtubeId(url);
  if (yt) {
    return (
      <div className="aspect-video w-full overflow-hidden rounded-2xl">
        <iframe data-testid="candidate-video" src={`https://www.youtube.com/embed/${yt}`} title={label}
          className="h-full w-full" allow="accelerometer; autoplay; encrypted-media; picture-in-picture" allowFullScreen />
      </div>
    );
  }
  if (url) {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" data-testid="candidate-video-link"
        className="flex items-center justify-center gap-2 rounded-2xl border border-brand/20 bg-brand-light/50 px-6 py-8 text-sm font-bold text-brand">
        <Play className="h-5 w-5" /> {label} <ExternalLink className="h-4 w-4" />
      </a>
    );
  }
  return null;
};

const Countdown = ({ target, label, c }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, new Date(target).getTime() - now);
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  const cells = [[d, c.days], [h, c.hours], [m, c.minutes], [s, c.seconds]];
  return (
    <div data-testid="concours-countdown" className="rounded-3xl border border-gold/30 bg-white/5 p-6 backdrop-blur-md">
      <div className="flex items-center gap-2 font-mono text-xs font-bold uppercase tracking-[0.25em] text-gold">
        <Clock className="h-4 w-4" /> {label}
      </div>
      <div className="mt-4 flex gap-3">
        {cells.map(([v, u]) => (
          <div key={u} className="min-w-[64px] rounded-2xl bg-brand-deep/80 px-3 py-3 text-center">
            <div className="font-display text-2xl sm:text-3xl font-extrabold text-white">{String(v).padStart(2, "0")}</div>
            <div className="mt-1 font-mono text-[10px] uppercase tracking-widest text-white/50">{u}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const PHASE_STYLE = {
  a_venir: "bg-slate-100 text-slate-500",
  ouverte: "bg-brand text-white",
  cloturee: "bg-gold-glow text-gold",
  publiee: "bg-gold text-brand-deep",
};

/** Vote en 2 étapes : email → code OTP reçu par mail → vote enregistré. */
const VoteDialog = ({ cand, slug, c, lang, onClose, onVoted }) => {
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const requestCode = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/concours/${slug}/vote/demander-code`, { candidature_id: cand.id, email: email.trim() });
      setStep("code");
      toast.success(c.codeSent);
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const confirm = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/concours/${slug}/vote/confirmer`, { candidature_id: cand.id, email: email.trim(), code: code.trim() });
      toast.success(c.voted);
      onVoted();
      onClose();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Erreur");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[95] grid place-items-center bg-brand-deep/70 p-4 backdrop-blur-sm" onClick={onClose}>
      <div data-testid="vote-dialog" className="w-full max-w-md rounded-3xl bg-white p-7 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3.5">
            {cand.photo_url && <img src={`${BACKEND}${cand.photo_url}`} alt="" className="h-14 w-14 rounded-xl object-cover" />}
            <div>
              <div className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-brand">{c.voteDialogTitle}</div>
              <div className="font-display text-lg font-extrabold text-slate-900">N°{String(cand.numero).padStart(2, "0")} — {cand.prenom} {cand.nom}</div>
            </div>
          </div>
          <button onClick={onClose} data-testid="vote-dialog-close" className="rounded-full p-2 text-slate-400 hover:bg-slate-100" aria-label={c.close}>
            <X className="h-5 w-5" />
          </button>
        </div>
        <p className="mt-4 rounded-xl bg-brand-light/60 px-4 py-3 text-xs font-semibold text-brand">{c.voteFreeNote}</p>

        {step === "email" ? (
          <form onSubmit={requestCode} className="mt-5">
            <label htmlFor="vote-email" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">{c.emailLabel}</label>
            <input id="vote-email" type="email" required data-testid="vote-email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder={c.emailPh} autoComplete="email" inputMode="email"
              className="w-full rounded-xl border border-brand/15 bg-white px-4 py-3 text-base sm:text-sm text-slate-900 outline-none transition-[border-color,box-shadow] duration-300 focus:border-brand focus:ring-2 focus:ring-brand/15" />
            <p className="mt-2 text-xs text-slate-400">{c.emailPrivacy}</p>
            <button type="submit" disabled={busy} data-testid="vote-send-code"
              className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand px-6 py-3.5 font-display font-bold text-white transition-colors hover:bg-brand-hover disabled:opacity-60">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Vote className="h-5 w-5" />}
              {busy ? c.sendingCode : c.sendCode}
            </button>
          </form>
        ) : (
          <form onSubmit={confirm} className="mt-5">
            <label htmlFor="vote-code" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">{c.codeLabel}</label>
            <input id="vote-code" required data-testid="vote-code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder={c.codePh} inputMode="numeric" autoComplete="one-time-code" maxLength={6}
              className="w-full rounded-xl border border-brand/15 bg-white px-4 py-3 text-center font-mono text-2xl font-bold tracking-[0.5em] text-slate-900 outline-none transition-[border-color,box-shadow] duration-300 focus:border-brand focus:ring-2 focus:ring-brand/15" />
            <button type="submit" disabled={busy || code.length !== 6} data-testid="vote-confirm"
              className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-gold px-6 py-3.5 font-display font-bold text-brand-deep transition-colors hover:bg-gold-warm disabled:opacity-60">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <CheckCircle2 className="h-5 w-5" />}
              {busy ? c.confirming : c.confirmVote}
            </button>
            <div className="mt-3 flex items-center justify-between text-xs">
              <button type="button" onClick={() => setStep("email")} data-testid="vote-back" className="font-semibold text-slate-400 hover:text-slate-600">{c.back}</button>
              <button type="button" onClick={requestCode} disabled={busy} data-testid="vote-resend" className="font-semibold text-brand hover:underline">{c.resend}</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default function Concours() {
  const { lang } = useLang();
  const c = COPY[lang];
  usePageTitle(c.meta);
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [selected, setSelected] = useState(null);
  const [voteCand, setVoteCand] = useState(null);
  const [searchParams] = useSearchParams();

  const load = useCallback(() => {
    axios.get(`${API}/concours/current`)
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true));
  }, []);
  useEffect(load, [load]);

  // Résultats en direct : actualisation automatique pendant la phase de vote
  useEffect(() => {
    if (!data?.votes_ouverts) return;
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [data?.votes_ouverts, load]);

  // Lien profond : /concours?candidate=05 ouvre directement la fiche partagée
  useEffect(() => {
    const num = searchParams.get("candidate");
    if (num && data?.candidates?.length) {
      const found = data.candidates.find((cd) => String(cd.numero).padStart(2, "0") === num.padStart(2, "0"));
      if (found) setSelected(found);
    }
  }, [searchParams, data]);

  const candidateUrl = (cand) => `${window.location.origin}/concours?candidate=${String(cand.numero).padStart(2, "0")}`;

  const shareWhatsApp = (cand) => {
    const url = candidateUrl(cand);
    const msg = c.shareMsg(cand, data.event.nom, url);
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank", "noopener,noreferrer");
  };

  if (notFound) {
    return (
      <div data-testid="concours-empty" className="grid min-h-screen place-items-center bg-ivory px-6 pt-20">
        <div className="text-center">
          <Trophy className="mx-auto h-14 w-14 text-gold" />
          <h1 className="mt-6 font-display text-3xl font-extrabold text-slate-900">{c.noneTitle}</h1>
          <p className="mt-3 text-slate-500">{c.noneText}</p>
          <Link to="/" data-testid="concours-home-link" className="mt-8 inline-flex items-center gap-2 rounded-full bg-brand px-7 py-3.5 font-display font-bold text-white transition-colors hover:bg-brand-hover">
            {c.home}
          </Link>
        </div>
      </div>
    );
  }

  if (!data) {
    return <div className="grid min-h-screen place-items-center bg-brand-deep pt-20 text-sm text-white/50">{c.loading}</div>;
  }

  const { event, candidates, phases, phase_courante, gagnante, inscriptions_ouvertes, votes_ouverts } = data;
  const phaseEnCours = phases.find((p) => p.id === phase_courante);
  const countdownTarget = phaseEnCours
    ? (votes_ouverts ? { t: phaseEnCours.date_fin, l: c.endsIn } : { t: phaseEnCours.date_debut, l: c.startsIn })
    : null;
  const prix = event.prix_montant?.toLocaleString("fr-FR");

  return (
    <div data-testid="concours-page">
      {/* Hero */}
      <section className="relative overflow-hidden bg-brand-deep text-white">
        {event.cover_url && (
          <img src={`${BACKEND}${event.cover_url}`} alt={event.nom} className="absolute inset-0 h-full w-full object-cover opacity-25" />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-brand-deep/60 to-brand-deep" />
        <div className="pointer-events-none absolute -top-32 right-[-5%] h-96 w-96 rounded-full bg-gold/20 blur-[110px]" />
        <div className="relative mx-auto max-w-7xl px-6 pb-16 pt-32 lg:pt-40">
          <div className="flex flex-col items-start gap-10 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              {event.logo_url && (
                <img src={`${BACKEND}${event.logo_url}`} alt="" data-testid="concours-logo" className="mb-6 h-16 w-16 rounded-2xl bg-white/95 object-contain p-1.5 shadow-lg" />
              )}
              <div className="mb-5 flex items-center gap-2 font-mono text-xs font-bold uppercase tracking-[0.3em] text-gold">
                <Trophy className="h-4 w-4" /> {c.voteLoginFree}
              </div>
              <MaskedLines lines={[event.nom]} className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.06] text-balance" />
              {event.description && <p className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-white/65">{event.description}</p>}
              {event.prix_montant > 0 && (
                <div data-testid="concours-prize" className="mt-7 inline-flex items-center gap-3 rounded-2xl border border-gold/40 bg-gold/10 px-6 py-4">
                  <Crown className="h-7 w-7 text-gold" />
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">{c.prize}</div>
                    <div className="font-display text-2xl font-extrabold text-gold">{prix} {event.prix_devise}</div>
                  </div>
                </div>
              )}
              <div className="mt-8 flex flex-wrap gap-4">
                {inscriptions_ouvertes && (
                  <Link to="/concours/inscription" data-testid="concours-register-cta"
                    className="inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3.5 font-display font-bold text-brand-deep transition-[background-color,transform] duration-300 hover:bg-gold-warm hover:-translate-y-0.5">
                    <Users className="h-5 w-5" /> {c.register}
                  </Link>
                )}
                {event.reglement && (
                  <a href="#reglement" data-testid="concours-rules-link"
                    className="inline-flex items-center gap-2 rounded-full border border-white/25 px-7 py-3.5 font-display font-bold text-white transition-colors hover:bg-white/10">
                    <ScrollText className="h-5 w-5 text-gold" /> {c.rules}
                  </a>
                )}
              </div>
            </div>
            {countdownTarget && <Countdown target={countdownTarget.t} label={countdownTarget.l} c={c} />}
          </div>
        </div>
      </section>

      {/* Gagnante */}
      {gagnante && (
        <section data-testid="gagnante-section" className="bg-gold py-16">
          <div className="mx-auto flex max-w-4xl flex-col items-center gap-6 px-6 text-center">
            <Crown className="h-12 w-12 text-brand-deep" />
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-brand-deep">{c.winner}</h2>
            {gagnante.photo_url && (
              <img src={`${BACKEND}${gagnante.photo_url}`} alt={gagnante.nom} className="h-48 w-48 rounded-full border-4 border-white object-cover shadow-2xl" />
            )}
            <p className="font-display text-2xl font-bold text-brand-deep">
              n°{String(gagnante.numero).padStart(2, "0")} — {gagnante.prenom} {gagnante.nom}
            </p>
            <p className="text-brand-deep/70">{c.winnerSub(prix, event.prix_devise)}</p>
          </div>
        </section>
      )}

      {/* Candidates */}
      {candidates.length > 0 && (
        <section className="bg-ivory py-20 sm:py-24">
          <div className="mx-auto max-w-7xl px-6">
            <Reveal>
              <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">{c.candidates}</h2>
              <p className="mt-3 text-base text-slate-600">{c.candidatesSub}</p>
              {!votes_ouverts && phaseEnCours && <p className="mt-2 text-sm font-semibold text-gold">{c.votesClosed}</p>}
              {phaseEnCours && !phaseEnCours.scores_visibles && <p className="mt-2 text-sm text-slate-500">{c.hiddenScores}</p>}
            </Reveal>
            <div className="mt-12 grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4">
              {candidates.map((cand, i) => {
                const enCourse = phaseEnCours && phaseEnCours.statut === "ouverte";
                return (
                  <Reveal key={cand.id} delay={i * 0.04}>
                    <div data-testid={`candidate-card-${cand.numero}`}
                      className="group overflow-hidden rounded-3xl border border-brand/10 bg-white shadow-sm transition-[transform,box-shadow] duration-300 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-brand/10">
                      <button onClick={() => setSelected(cand)} data-testid={`candidate-open-${cand.numero}`} className="block w-full text-left">
                        <div className="relative aspect-[3/4] overflow-hidden bg-brand-light">
                          {cand.photo_url && (
                            <img src={`${BACKEND}${cand.photo_url}`} alt={`${cand.prenom} ${cand.nom}`} loading="lazy"
                              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                          )}
                          <span className="absolute left-3 top-3 grid h-10 w-10 place-items-center rounded-full bg-brand-deep/90 font-display text-sm font-extrabold text-gold shadow-lg">
                            {String(cand.numero).padStart(2, "0")}
                          </span>
                          {cand.votes !== null && (
                            <span data-testid={`candidate-votes-${cand.numero}`} className="absolute bottom-3 right-3 rounded-full bg-white/95 px-3 py-1 font-mono text-xs font-bold text-brand shadow">
                              {cand.votes} {c.votesUnit}
                            </span>
                          )}
                        </div>
                        <div className="p-4">
                          <div className="font-display text-base font-bold text-slate-900">{cand.prenom} {cand.nom}</div>
                          <div className="text-xs text-slate-500">{cand.ville}</div>
                          {cand.rang != null && (
                            <div data-testid={`candidate-rang-${cand.numero}`} className="mt-1 text-xs font-bold text-gold">
                              {c.rankLabel} : {formatRang(cand.rang, lang)}
                            </div>
                          )}
                        </div>
                      </button>
                      <div className="flex gap-2 px-4 pb-4">
                        <button
                          onClick={() => setVoteCand(cand)}
                          disabled={!votes_ouverts || !enCourse}
                          data-testid={`vote-btn-${cand.numero}`}
                          className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-brand px-4 py-2.5 font-display text-sm font-bold text-white transition-[background-color,transform] duration-300 hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          <Vote className="h-4 w-4" /> {c.vote}
                        </button>
                        <button
                          onClick={() => shareWhatsApp(cand)}
                          data-testid={`share-btn-${cand.numero}`}
                          aria-label={`${c.share} — ${cand.prenom} ${cand.nom}`}
                          title={`${c.share} sur WhatsApp`}
                          className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-[#25D366]/40 text-[#25D366] transition-[background-color,transform] duration-300 hover:bg-[#25D366] hover:text-white hover:scale-105"
                        >
                          <Share2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Phases & résultats */}
      <section className="bg-white py-20 sm:py-24">
        <div className="mx-auto max-w-4xl px-6">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">{c.phases}</h2>
          </Reveal>
          <div className="mt-12 space-y-6">
            {phases.map((p) => (
              <Reveal key={p.id}>
                <div data-testid={`phase-card-${p.ordre}`} className={`rounded-3xl border p-7 transition-colors ${p.id === phase_courante ? "border-gold/50 bg-gold-glow/30" : "border-brand/10 bg-ivory"}`}>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-3">
                        <span className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-brand">{c.phaseOf(p.ordre)}</span>
                        <span className={`rounded-full px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-widest ${PHASE_STYLE[p.statut]}`}>{p.statut}</span>
                      </div>
                      <h3 className="mt-2 font-display text-xl font-bold text-slate-900">{p.nom}</h3>
                      {p.description && <p className="mt-1 text-sm text-slate-600">{p.description}</p>}
                    </div>
                    <div className="text-right text-xs text-slate-500">
                      {p.type === "proclamation" ? (
                        <div className="flex items-center justify-end gap-1.5 font-semibold text-gold"><Crown className="h-3.5 w-3.5" /> {lang === "en" ? "Official proclamation" : "Proclamation officielle"}</div>
                      ) : (
                        <>
                          <div className="flex items-center gap-1.5"><Users className="h-3.5 w-3.5 text-brand" /> {c.participants(p.nb_participants)}</div>
                          {p.total_votes !== null && <div className="mt-1 flex items-center justify-end gap-1.5"><Vote className="h-3.5 w-3.5 text-gold" /> {c.votesTotal(p.total_votes)}</div>}
                        </>
                      )}
                      {p.date_debut && (
                        <div className="mt-1 flex items-center justify-end gap-1.5">
                          <Calendar className="h-3.5 w-3.5 text-brand" />
                          {new Date(p.date_debut).toLocaleDateString(lang === "en" ? "en-GB" : "fr-FR", { day: "numeric", month: "short" })}
                          {" → "}
                          {new Date(p.date_fin).toLocaleDateString(lang === "en" ? "en-GB" : "fr-FR", { day: "numeric", month: "short" })}
                        </div>
                      )}
                    </div>
                  </div>
                  {p.classement && (
                    <div data-testid={`classement-phase-${p.ordre}`} className="mt-6 border-t border-brand/10 pt-5">
                      <div className="mb-3 flex items-center gap-2 font-mono text-xs font-bold uppercase tracking-[0.25em] text-brand">
                        <CheckCircle2 className="h-4 w-4" /> {c.results}
                      </div>
                      <ol className="space-y-2">
                        {p.classement.map((r) => (
                          <li key={r.candidature_id} className={`flex items-center justify-between rounded-xl px-4 py-2.5 text-sm ${r.qualifie ? "bg-brand/5 font-semibold" : "text-slate-500"}`}>
                            <span className="flex items-center gap-3">
                              <span className={`grid h-7 w-7 place-items-center rounded-full font-mono text-xs font-bold ${r.rang === 1 ? "bg-gold text-brand-deep" : "bg-slate-100 text-slate-600"}`}>{r.rang}</span>
                              n°{String(r.numero).padStart(2, "0")} — {r.prenom} {r.nom}
                              {r.qualifie && <span className="rounded-full bg-brand px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">{c.qualified}</span>}
                            </span>
                            <span className="font-mono font-bold text-brand">{r.votes} {c.votesUnit}</span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}
                  {p.statut === "cloturee" && !p.classement && (
                    <p className="mt-4 text-sm italic text-slate-500">{c.pendingResults}</p>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Règlement */}
      {(event.reglement || event.conditions) && (
        <section id="reglement" className="bg-brand-deep py-20 text-white">
          <div className="mx-auto grid max-w-6xl grid-cols-1 gap-10 px-6 lg:grid-cols-2">
            {event.reglement && (
              <Reveal>
                <h2 className="flex items-center gap-3 font-display text-2xl font-extrabold"><ScrollText className="h-6 w-6 text-gold" /> {c.rules}</h2>
                <p className="mt-5 whitespace-pre-line text-sm leading-relaxed text-white/65">{event.reglement}</p>
              </Reveal>
            )}
            {event.conditions && (
              <Reveal delay={0.1}>
                <h2 className="flex items-center gap-3 font-display text-2xl font-extrabold"><CheckCircle2 className="h-6 w-6 text-gold" /> {c.conditions}</h2>
                <p className="mt-5 whitespace-pre-line text-sm leading-relaxed text-white/65">{event.conditions}</p>
              </Reveal>
            )}
          </div>
        </section>
      )}

      {/* Fiche candidate (modale) */}
      {selected && (
        <div className="fixed inset-0 z-[90] grid place-items-center overflow-y-auto bg-brand-deep/70 p-4 backdrop-blur-sm" onClick={() => setSelected(null)}>
          <div data-testid="candidate-modal" className="my-8 w-full max-w-lg rounded-3xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="relative">
              {selected.photo_url && (
                <img src={`${BACKEND}${selected.photo_url}`} alt={selected.nom} className="aspect-[4/3] w-full rounded-t-3xl object-cover" />
              )}
              <span className="absolute left-4 top-4 grid h-12 w-12 place-items-center rounded-full bg-brand-deep/90 font-display font-extrabold text-gold shadow-lg">
                {String(selected.numero).padStart(2, "0")}
              </span>
              <button onClick={() => setSelected(null)} data-testid="candidate-modal-close" aria-label={c.close}
                className="absolute right-4 top-4 grid h-10 w-10 place-items-center rounded-full bg-white/90 text-slate-700 shadow transition-colors hover:bg-white">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-7">
              <h3 className="font-display text-2xl font-extrabold text-slate-900">{selected.prenom} {selected.nom}</h3>
              <p className="text-sm text-slate-500">{selected.ville}</p>
              <div className="mt-5">
                <div className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-brand">{c.bio}</div>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{selected.bio}</p>
              </div>
              <div className="mt-4">
                <div className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-brand">{c.motivation}</div>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{selected.motivation}</p>
              </div>
              {(selected.video_file_url || selected.video_url) && (
                <div className="mt-5">
                  <VideoBlock url={selected.video_url} fileUrl={selected.video_file_url} label={c.watchVideo} />
                </div>
              )}
              <button
                onClick={() => setVoteCand(selected)}
                disabled={!votes_ouverts}
                data-testid="modal-vote-btn"
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand px-6 py-4 font-display font-bold text-white transition-[background-color] duration-300 hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Vote className="h-5 w-5" />
                {c.vote} — n°{String(selected.numero).padStart(2, "0")}
              </button>
              <button
                onClick={() => shareWhatsApp(selected)}
                data-testid="modal-share-btn"
                className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] px-6 py-4 font-display font-bold text-white transition-[background-color] duration-300 hover:bg-[#1fb857]"
              >
                <Share2 className="h-5 w-5" />
                {c.share} sur WhatsApp
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Vote sécurisé par code email (OTP) */}
      {voteCand && (
        <VoteDialog
          cand={voteCand}
          slug={event.slug}
          c={c}
          lang={lang}
          onClose={() => setVoteCand(null)}
          onVoted={load}
        />
      )}
    </div>
  );
}
